import { formatDisplayDateTime } from "./displayFormat.mjs";
export const DEFAULT_EXTERNAL_API_MAPPING = Object.freeze({
  version: 1,
  field_mappings: [],
  default_values: [],
  transforms: [],
});

export const DEFAULT_EXTERNAL_API_RATE_LIMIT = Object.freeze({
  requests_per_minute: 60,
});

const deepClone = (value) => JSON.parse(JSON.stringify(value));

export const prettyPrintExternalApiJson = (value, fallback = "{}") => {
  try {
    return JSON.stringify(value ?? JSON.parse(fallback), null, 2);
  } catch (_) {
    return fallback;
  }
};

export const parseExternalApiJsonText = (value, fallback) => {
  const normalized = String(value || "").trim();
  if (!normalized) return deepClone(fallback);
  return JSON.parse(normalized);
};

export const createExternalApiEndpointDraft = (template = null) => ({
  id: null,
  name: "",
  endpoint_key: "",
  description: "",
  status: "draft",
  entity_scope: template?.entity_scope || "company",
  source_system: "",
  allow_source_system_override: false,
  authentication_mode: "x_api_key",
  payload_mode: "single_object",
  content_type: "application/json",
  routing_mode: "store_only",
  template_key: template?.key || null,
  mapping_config_json: deepClone(template?.mapping_config_json || DEFAULT_EXTERNAL_API_MAPPING),
  rate_limit_config_json: deepClone(DEFAULT_EXTERNAL_API_RATE_LIMIT),
  active_key_count: 0,
  delivery_count_24h: 0,
  failed_count_24h: 0,
  last_delivery_at: null,
  created_at: null,
  updated_at: null,
});

export const cloneExternalApiEndpoint = (endpoint = null) => {
  if (!endpoint) return createExternalApiEndpointDraft();
  return {
    ...createExternalApiEndpointDraft(),
    ...deepClone(endpoint),
    mapping_config_json: deepClone(endpoint.mapping_config_json || DEFAULT_EXTERNAL_API_MAPPING),
    rate_limit_config_json: deepClone(endpoint.rate_limit_config_json || DEFAULT_EXTERNAL_API_RATE_LIMIT),
  };
};

export const findExternalApiTemplate = (templates = [], templateKey = null) =>
  (Array.isArray(templates) ? templates : []).find((template) => template.key === templateKey) || null;

export const buildExternalApiEndpointPayload = (form = {}, { mappingText = "{}", rateLimitPerMinute = 60 } = {}) => {
  const mappingConfig = parseExternalApiJsonText(mappingText, DEFAULT_EXTERNAL_API_MAPPING);
  return {
    name: String(form.name || "").trim(),
    endpoint_key: String(form.endpoint_key || "").trim(),
    description: String(form.description || "").trim(),
    status: String(form.status || "draft").trim(),
    entity_scope: String(form.entity_scope || "company").trim(),
    source_system: String(form.source_system || "").trim(),
    allow_source_system_override: Boolean(form.allow_source_system_override),
    template_key: form.template_key || null,
    mapping_config_json: mappingConfig,
    rate_limit_config_json: {
      requests_per_minute: Math.max(1, Number.parseInt(rateLimitPerMinute, 10) || 60),
    },
  };
};

export const formatExternalApiDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return formatDisplayDateTime(parsed);
};

export const buildExternalApiSamplePayload = (endpoint = {}) => {
  const scope = String(endpoint.entity_scope || "company").trim().toLowerCase();
  const sourceSystem = String(endpoint.source_system || "").trim() || "crm-a";
  if (scope === "person") {
    return {
      external_id: "person-123",
      source_system: sourceSystem,
      event_type: "person.created",
      occurred_at: "2026-04-22T12:00:00Z",
      payload: {
        full_name: "Ana Popescu",
        email: "ana@example.com",
        phone: "+37360000000",
      },
      metadata: {
        source_user: "sync-bot",
      },
    };
  }

  return {
    external_id: "company-123",
    source_system: sourceSystem,
    event_type: "company.created",
    occurred_at: "2026-04-22T12:00:00Z",
    payload: {
      name: "Acme SRL",
      website: "https://acme.example",
      phone: "+37360000000",
    },
    metadata: {
      source_user: "sync-bot",
    },
  };
};

export const buildExternalApiSampleCurl = (endpoint = {}, samplePayload = null) => {
  const endpointKey = String(endpoint.endpoint_key || "<endpointKey>").trim() || "<endpointKey>";
  const payload = samplePayload || buildExternalApiSamplePayload(endpoint);
  return [
    `curl -X POST "http://localhost:3001/integrations/inbound/${endpointKey}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -H "x-api-key: <secret>" \\`,
    `  -H "x-idempotency-key: evt-001" \\`,
    `  -d '${JSON.stringify(payload, null, 2)}'`,
  ].join("\n");
};

export const filterExternalApiEndpoints = (endpoints = [], { search = "", status = "all" } = {}) => {
  const normalizedSearch = String(search || "").trim().toLowerCase();
  const normalizedStatus = String(status || "all").trim().toLowerCase();
  return (Array.isArray(endpoints) ? endpoints : []).filter((endpoint) => {
    const matchesStatus = normalizedStatus === "all" || String(endpoint.status || "").toLowerCase() === normalizedStatus;
    if (!matchesStatus) return false;
    if (!normalizedSearch) return true;
    const haystack = [
      endpoint.name,
      endpoint.endpoint_key,
      endpoint.entity_scope,
      endpoint.source_system,
      endpoint.template_key,
    ]
      .map((value) => String(value || "").toLowerCase())
      .join(" ");
    return haystack.includes(normalizedSearch);
  });
};

export const filterExternalApiDeliveries = (deliveries = [], { search = "", status = "all" } = {}) => {
  const normalizedSearch = String(search || "").trim().toLowerCase();
  const normalizedStatus = String(status || "all").trim().toLowerCase();
  return (Array.isArray(deliveries) ? deliveries : []).filter((delivery) => {
    const matchesStatus = normalizedStatus === "all" || String(delivery.status || "").toLowerCase() === normalizedStatus;
    if (!matchesStatus) return false;
    if (!normalizedSearch) return true;
    const haystack = [
      delivery.external_id,
      delivery.endpoint_name,
      delivery.endpoint_key,
      delivery.source_system,
      delivery.event_type,
      delivery.error_code,
      delivery.error_message,
      delivery.idempotency_key,
    ]
      .map((value) => String(value || "").toLowerCase())
      .join(" ");
    return haystack.includes(normalizedSearch);
  });
};

export const resolveExternalApiStatusMeta = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "active" || normalized === "stored") return { color: "success", variant: "tonal" };
  if (normalized === "draft") return { color: "info", variant: "tonal" };
  if (normalized === "paused" || normalized === "revoked") return { color: "warning", variant: "tonal" };
  if (normalized === "archived" || normalized === "deleted") return { color: "neutral", variant: "tonal" };
  if (normalized === "failed" || normalized === "rejected") return { color: "error", variant: "tonal" };
  return { color: "neutral", variant: "tonal" };
};

// S5.4 — starea de protecția datelor a unei livrări din formular public.
const DELIVERY_PRIVACY_COLORS = Object.freeze({
  linked: "success",
  captured: "info",
  pending_confirmation: "warning",
  review_required: "warning",
  failed: "error",
});
export const resolveDeliveryPrivacyMeta = (indicator) => ({
  color: DELIVERY_PRIVACY_COLORS[indicator] || "neutral",
  variant: "tonal",
});
